"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowUpRight,
  Award,
  Check,
  GraduationCap,
  Handshake,
  HeartHandshake,
  Lightbulb,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
  Users,
} from "lucide-react";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

interface CorporateValuesProps {
  locale?: Locale;
}

export default function CorporateValues({
  locale = defaultLocale,
}: CorporateValuesProps) {
  const prefersReducedMotion = useReducedMotion();
  const translate = (text: string) => t(locale, text);

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.7, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const values: Array<{
    title: string;
    icon: LucideIcon;
    description: string;
    principles: string[];
  }> = [
    {
      title: "Client-Centricity",
      icon: Users,
      description:
        "We put our clients at the center of everything we do, listening deeply to their needs and working collaboratively to deliver solutions that drive meaningful business outcomes.",
      principles: [
        "Deep understanding of client needs",
        "Responsive communication",
        "Tailored solutions",
        "Long-term partnership focus",
      ],
    },
    {
      title: "Innovation",
      icon: Lightbulb,
      description:
        "We embrace creative thinking and continuous exploration of emerging technologies to develop innovative solutions that address complex business challenges.",
      principles: [
        "Proactive technology exploration",
        "Creative problem-solving",
        "Continuous improvement",
        "Forward-thinking approach",
      ],
    },
    {
      title: "Excellence",
      icon: Award,
      description:
        "We are committed to delivering excellence in everything we do, maintaining the highest standards of quality, professionalism, and technical expertise.",
      principles: [
        "Rigorous quality standards",
        "Attention to detail",
        "Continuous learning",
        "Professional development",
      ],
    },
    {
      title: "Integrity",
      icon: ShieldCheck,
      description:
        "We operate with unwavering integrity, building trust through honesty, transparency, and ethical business practices in all our client and partner relationships.",
      principles: [
        "Ethical decision-making",
        "Transparent communication",
        "Responsible data handling",
        "Honest client guidance",
      ],
    },
    {
      title: "Collaboration",
      icon: Handshake,
      description:
        "We believe in the power of collaboration, fostering a culture of teamwork, knowledge sharing, and mutual respect within our company and with our clients.",
      principles: [
        "Cross-functional teamwork",
        "Inclusive decision-making",
        "Knowledge sharing",
        "Client partnership",
      ],
    },
    {
      title: "Adaptability",
      icon: RefreshCw,
      description:
        "We embrace change and maintain the agility to adapt quickly to evolving client needs, market conditions, and technological advancements.",
      principles: [
        "Flexible methodology",
        "Resilient mindset",
        "Continuous learning",
        "Rapid response capability",
      ],
    },
  ];

  const valuesInAction: Array<{
    title: string;
    icon: LucideIcon;
    description: string;
    href?: string;
    linkLabel?: string;
  }> = [
    {
      title: "Client Success Stories",
      icon: TrendingUp,
      description:
        "Our values have helped us deliver transformative solutions that have created measurable impact for our clients across various industries.",
      href: "/blog/agentic-ai-workflow-automation-malaysian-business/",
      linkLabel: "Read a client story",
    },
    {
      title: "Team Development",
      icon: GraduationCap,
      description:
        "We invest in our team's growth, providing continuous learning opportunities that align with our values of excellence and innovation.",
    },
    {
      title: "Responsible Practices",
      icon: ShieldCheck,
      description:
        "Our integrity value guides our approach to data security, privacy, and ethical technology implementation for our clients.",
      href: "/services/ai-solutions/",
      linkLabel: "See how we build responsibly",
    },
    {
      title: "Community Engagement",
      icon: HeartHandshake,
      description:
        "We actively contribute to our communities through technology education initiatives and nonprofit support.",
    },
  ];

  return (
    <div className="bg-background">
      {/* Unified page header */}
      <section className="bg-background pb-16 pt-24 md:pb-20 md:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...reveal(0)}
            className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
          >
            {translate("Corporate Values")}
          </motion.p>
          <motion.h1
            {...reveal(0.08)}
            className="max-w-4xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl"
          >
            {translate("The principles behind")}{" "}
            <span className="emphasis-underline">
              {translate("every sprint")}
            </span>
          </motion.h1>
          <motion.p
            {...reveal(0.16)}
            className="mt-5 max-w-2xl text-lg text-neutral-muted"
          >
            {translate(
              "At Anchor Sprint, our core values define who we are and guide how we operate. These principles shape our culture, inform our decisions, and drive our commitment to delivering exceptional value to our clients.",
            )}
          </motion.p>
        </div>
      </section>
      <div className="h-px w-full bg-border" aria-hidden />

      {/* Core values — numbered cards */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("What guides us")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("Our Core Values")}
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {values.map(
              ({ title, description, icon: Icon, principles }, index) => (
                <motion.div
                  key={title}
                  initial={prefersReducedMotion ? false : { y: 12 }}
                  whileInView={{ y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.08,
                    ease: [0.21, 0.6, 0.35, 1],
                  }}
                  className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
                >
                  <div className="mb-6 flex items-start justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" aria-hidden />
                    </div>
                    <span className="font-mono text-sm text-neutral-muted/60">
                      0{index + 1}
                    </span>
                  </div>

                  <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                    {translate(title)}
                  </h3>
                  <p className="mb-6 text-sm leading-relaxed text-neutral-muted">
                    {translate(description)}
                  </p>

                  <ul className="mt-auto space-y-2">
                    {principles.map((principle) => (
                      <li
                        key={principle}
                        className="flex items-start gap-2 text-sm text-neutral-muted"
                      >
                        <Check
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
                          aria-hidden
                        />
                        {translate(principle)}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* Values in action */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("Proof in practice")}
            </p>
            <h2 className="mb-4 font-display text-3xl font-bold text-neutral-emphasis md:text-4xl">
              {translate("Our Values in Action")}
            </h2>
            <p className="max-w-3xl text-base leading-relaxed text-neutral-muted md:text-lg">
              {translate(
                "Our values are not just words on a page — they shape how we work every day, and they show up in the work itself.",
              )}
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
            {valuesInAction.map(
              ({ title, description, icon: Icon, href, linkLabel }, index) => (
                <motion.div
                  key={title}
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.08,
                    ease: [0.21, 0.6, 0.35, 1],
                  }}
                  className="flex h-full items-start gap-5 rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-neutral-border"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <div>
                    <h3 className="mb-2 font-display text-base font-semibold text-neutral-emphasis">
                      {translate(title)}
                    </h3>
                    <p className="text-sm leading-relaxed text-neutral-muted">
                      {translate(description)}
                    </p>
                    {href && linkLabel && (
                      <Link
                        href={getLocalizedPathname(href, locale)}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 hover:gap-2.5"
                      >
                        {translate(linkLabel)}
                        <ArrowUpRight className="h-4 w-4" aria-hidden />
                      </Link>
                    )}
                  </div>
                </motion.div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-neutral-emphasis md:text-5xl">
            {translate("Put these principles")}{" "}
            <span className="emphasis-underline">
              {translate("to work")}
            </span>
          </h2>
          <p className="mt-5 text-lg text-neutral-muted">
            {translate(
              "See how our core values of client-centricity, innovation, integrity, and collaboration translate into exceptional results for your business.",
            )}
          </p>
          <div className="mt-9">
            <Link
              href={getLocalizedPathname("/contact/", locale)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {translate("Meet with Us")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
